import { useState } from 'react';
import { api, type MeResponse } from '../api';
import { signOutFederated } from '../auth';
import { messageFor } from '../App';

/**
 * What a signed-in identity sees when it belongs to no account.
 *
 * Being here is not an error, and there is nothing to fill in: an invited person is joined
 * during sign-in, so anyone who reaches this screen genuinely has not been invited, or their
 * invite has lapsed. The useful thing to show them is who to ask.
 */
export function Gate({ me, onJoined }: { me: MeResponse; onJoined: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function create() {
    setBusy(true);
    setError(undefined);
    try {
      await api.createAccount({
        name: name.trim(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        jurisdictionId: 'MO',
      });
      onJoined();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <h1>unschool.works</h1>
      {error && <div className="notice error">{error}</div>}

      <div className="panel">
        <h2>
          {me.reason === 'expired'
            ? 'That invite has expired'
            : me.reason === 'already_used'
              ? 'That invite has already been used'
              : 'You have not been invited yet'}
        </h2>
        <p className="sub" style={{ marginBottom: 0 }}>
          {me.message}
        </p>
        <p style={{ marginTop: 12, marginBottom: 0 }}>
          {!me.contact || me.contact.toLowerCase() === me.identity.email.toLowerCase() ? (
            // Telling someone to ask themselves for an invite is nonsense, and it is exactly
            // what the operator sees on a fresh install or after a sign-in identity changes.
            <>
              You run this app, so there is nobody to ask. If you already have a family here,
              something has gone wrong linking this sign-in to it — check the server logs
              rather than starting a second one.
            </>
          ) : (
            <>
              Ask <a href={`mailto:${me.contact}`}>{me.contact}</a> to invite{' '}
              <strong>{me.identity.email}</strong>, then sign in again — you will not need a
              code or a link, just this same Google account.
            </>
          )}
        </p>
        <p className="muted" style={{ marginTop: 14 }}>
          Signed in as {me.identity.email}.
        </p>
        {/*
          This screen used to say "sign out and try another" and give no way to do it, which
          for an uninvited address was the only screen reachable -- a dead end. The button has
          to be here, not only in the header, because someone who lands here never sees the
          header.
        */}
        <button style={{ marginTop: 4 }} onClick={() => void signOutFederated()}>
          Sign out
        </button>
        <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
          Signing out ends your session here and sends you back to Google, where you can pick
          a different account. If Google takes you straight back in as the same person, that
          is the only account your browser is signed in to — add another at{' '}
          <a href="https://accounts.google.com/" target="_blank" rel="noreferrer">
            Google
          </a>{' '}
          and then sign in again.
        </p>
      </div>

      {me.canCreateAccount && (
        <div className="panel">
          <h2>Start a new family</h2>
          <p className="sub">
            Your address is on the bootstrap list, so you can create an account without an
            invite. Its time zone will be {Intl.DateTimeFormat().resolvedOptions().timeZone}.
          </p>
          <div className="field">
            <label htmlFor="acct">Family name</label>
            <input
              id="acct"
              placeholder="The Smith Family"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button className="primary" disabled={busy || !name.trim()} onClick={() => void create()}>
            Create account
          </button>
        </div>
      )}
    </div>
  );
}
